import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Switch, Tag } from 'antd';
import { notification } from '../../AntdGlobal';
import { listAdmins, createAdmin, updateAdmin, Admin } from '../../auth/api';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

const AdminsPage = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [form] = Form.useForm();

  const fetchAdmins = async () => {
    setLoading(true);
    const res = await listAdmins();
    if (res.ok) {
      setAdmins(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleCreate = () => {
    setEditingAdmin(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: Admin) => {
    setEditingAdmin(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingAdmin) {
        const payload = { ...values };
        if (!payload.password) {
          delete payload.password;
        }
        delete payload.username;

        const res = await updateAdmin(editingAdmin.id, payload);
        if (res.ok) {
          notification.success({ message: 'Success', description: 'Admin updated', duration: 5 });
          fetchAdmins();
          setIsModalVisible(false);
        }
      } else {
        const res = await createAdmin(values);
        if (res.ok) {
          notification.success({ message: 'Success', description: 'Admin created', duration: 5 });
          fetchAdmins();
          setIsModalVisible(false);
        }
      }
    } catch (error) {
      console.error('Validate Failed:', error);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Username', dataIndex: 'username', key: 'username' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean, record: Admin) => (
        <Switch
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          checked={isActive}
          onChange={async (checked) => {
            const res = await updateAdmin(record.id, { is_active: checked });
            if (res.ok) {
              notification.success({ message: 'Success', description: 'Status updated', duration: 5 });
              fetchAdmins();
            }
          }}
        />
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (ts: number) => ts ? new Date(ts * 1000).toLocaleString() : '-',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: Admin) => (
        <Button type="link" onClick={() => handleEdit(record)}>Edit</Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleCreate}>Create Admin</Button>
      </div>
      <Table dataSource={admins} columns={columns} rowKey="id" loading={loading} />

      <Modal
        title={editingAdmin ? "Edit Admin" : "Create Admin"}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Please input username!' }]}>
            <Input disabled={!!editingAdmin} />
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: !editingAdmin, message: 'Please input password!' }, { min: 8, message: 'Password must be at least 8 characters' }]}>
            <Input.Password placeholder={editingAdmin ? "Leave empty to keep current password" : ""} />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'The input is not valid E-mail!' }]}>
            <Input />
          </Form.Item>
          {editingAdmin && (
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default AdminsPage;
